import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API = "https://eps.elkomprintama.com/";

const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-400";
const lbl =
  "block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1";

function MaterialManagement() {
  const [materials, setMaterials] = useState([]);
  const [selectedMat, setSelectedMat] = useState(null);
  const [sizes, setSizes] = useState([]);

  const [newMatName, setNewMatName] = useState("");
  const [addingMat, setAddingMat] = useState(false);

  const [newSize, setNewSize] = useState("");
  const [addingSize, setAddingSize] = useState(false);

  const [loadingMats, setLoadingMats] = useState(true);
  const [loadingSizes, setLoadingSizes] = useState(false);

  const fetchMaterials = useCallback(async () => {
    setLoadingMats(true);
    try {
      const res = await axios.get(`${API}get/api/materials`, {
        withCredentials: true,
      });
      setMaterials(res.data.result || []);
    } catch {
      toast.error("Failed to load materials.");
    } finally {
      setLoadingMats(false);
    }
  }, []);

  const fetchSizes = useCallback(async (matId) => {
    setLoadingSizes(true);
    try {
      const res = await axios.get(`${API}get/api/mat-sizes/${matId}`, {
        withCredentials: true,
      });
      setSizes(res.data.result || []);
    } catch {
      toast.error("Failed to load sizes.");
    } finally {
      setLoadingSizes(false);
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const handleSelectMat = (mat) => {
    setSelectedMat(mat);
    setNewSize("");
    fetchSizes(mat.mat_id);
  };

  const handleAddMaterial = async (e) => {
    e.preventDefault();
    if (!newMatName.trim()) {
      toast.warning("Enter a material name.");
      return;
    }
    setAddingMat(true);
    try {
      await axios.post(
        `${API}post/api/material`,
        { mat_name: newMatName },
        { withCredentials: true },
      );
      toast.success("Material added!");
      setNewMatName("");
      fetchMaterials();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to add material.");
    } finally {
      setAddingMat(false);
    }
  };

  const handleAddSize = async (e) => {
    e.preventDefault();
    if (!newSize.trim()) {
      toast.warning("Enter a size value.");
      return;
    }
    setAddingSize(true);
    try {
      await axios.post(
        `${API}post/api/mat-size`,
        { mat_id: selectedMat.mat_id, mat_size: newSize },
        { withCredentials: true },
      );
      toast.success("Size added!");
      setNewSize("");
      fetchSizes(selectedMat.mat_id);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to add size.");
    } finally {
      setAddingSize(false);
    }
  };

  const handleDeleteSize = async (size_id) => {
    try {
      await axios.delete(`${API}delete/api/mat-size/${size_id}`, {
        withCredentials: true,
      });
      toast.success("Size removed.");
      fetchSizes(selectedMat.mat_id);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to delete size.");
    }
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      <div className="w-full rounded-lg bg-green-600 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Material Management
        </label>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Material list + Add material */}
        <div className="flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden min-h-0">
          <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
              Materials
            </h3>
          </div>

          {/* Add new material */}
          <form
            onSubmit={handleAddMaterial}
            className="px-5 py-4 border-b border-gray-100 bg-green-50 flex-shrink-0"
          >
            <label className={lbl}>Add New Material</label>
            <div className="flex gap-2">
              <input
                className={inp}
                name="newmatname" type="text"
                placeholder="e.g. PET, PE Foam, Copper Foil..."
                value={newMatName}
                onChange={(e) => setNewMatName(e.target.value)}
              />
              <button
                type="submit"
                disabled={addingMat}
                className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 transition"
              >
                {addingMat ? "Adding..." : "Add"}
              </button>
            </div>
          </form>

          {/* Material list */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loadingMats ? (
              <div className="text-center py-10 text-gray-400 text-sm">
                Loading...
              </div>
            ) : materials.length === 0 ? (
              <div className="text-center py-10 text-gray-400 italic text-sm">
                No materials found.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {materials.map((m) => (
                  <li key={m.mat_id}>
                    <button
                      type="button"
                      onClick={() => handleSelectMat(m)}
                      className={`w-full text-left px-5 py-3 text-sm transition flex items-center justify-between ${
                        selectedMat?.mat_id === m.mat_id
                          ? "bg-green-600 text-white font-semibold"
                          : "hover:bg-green-50 text-gray-700"
                      }`}
                    >
                      <span>{m.mat_name}</span>
                      <span
                        className={`text-xs ${selectedMat?.mat_id === m.mat_id ? "text-green-100" : "text-gray-400"}`}
                      >
                        ID: {m.mat_id}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: Sizes for selected material */}
        <div className="flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden min-h-0">
          <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
              {selectedMat
                ? `Sizes — ${selectedMat.mat_name}`
                : "Select a material to manage sizes"}
            </h3>
          </div>

          {!selectedMat ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-300">
              <svg
                className="w-12 h-12 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <p className="text-sm">Click a material on the left</p>
            </div>
          ) : (
            <>
              {/* Add size form */}
              <form
                onSubmit={handleAddSize}
                className="px-5 py-4 border-b border-gray-100 bg-green-50 flex-shrink-0"
              >
                <label className={lbl}>Add New Size</label>
                <div className="flex gap-2">
                  <input
                    className={inp}
                    name="newsize" type="text"
                    placeholder='e.g. 50mm, 0.05mm, 3"...'
                    value={newSize}
                    onChange={(e) => setNewSize(e.target.value)}
                  />
                  <button
                    type="submit"
                    disabled={addingSize}
                    className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 transition"
                  >
                    {addingSize ? "Adding..." : "Add"}
                  </button>
                </div>
              </form>

              {/* Sizes table */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {loadingSizes ? (
                  <div className="text-center py-10 text-gray-400 text-sm">
                    Loading sizes...
                  </div>
                ) : sizes.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 italic text-sm">
                    No sizes added yet.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
                          Size
                        </th>
                        <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">
                          Remove
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sizes.map((s) => (
                        <tr key={s.size_id} className="hover:bg-gray-50">
                          <td className="px-5 py-2.5 font-medium text-gray-700">
                            {s.mat_size}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <button
                              onClick={() => handleDeleteSize(s.size_id)}
                              className="px-2.5 py-1 rounded text-xs font-semibold text-red-500 border border-red-300 hover:bg-red-500 hover:text-white transition"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MaterialManagement;
